import { useState } from "react";
import { motion } from "framer-motion";
import { IMAGES } from "@/lib/data";
import { useContent } from "@/i18n/content";
import { useI18n } from "@/i18n";

const container = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.05 } },
};
const item = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] } },
};

export default function Recycling() {
  const [hovered, setHovered] = useState(null);
  const { WASTE_TYPES } = useContent();
  const { t } = useI18n();

  return (
    <section id="recycling" className="relative bg-[#F0EFEA] py-24 lg:py-32 overflow-hidden">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-12 lg:gap-16 items-center mb-16">
          <div>
            <span className="font-mono-tech text-xs uppercase tracking-[0.25em] text-emerald-600 font-semibold">
              {t("rec.eyebrow")}
            </span>
            <h2 className="mt-4 font-display font-extrabold uppercase tracking-tight text-3xl sm:text-4xl lg:text-5xl text-[#0B0F14] leading-[1.05]">
              {t("rec.title1")}<br />
              <span className="text-emerald-600">{t("rec.title2")}</span>
            </h2>
            <p className="mt-6 max-w-lg text-slate-600 leading-relaxed text-sm sm:text-base">
              {t("rec.blurb")}
            </p>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative rounded-3xl overflow-hidden aspect-[4/3] lg:aspect-square border border-slate-200 shadow-soft"
          >
            <img loading="lazy" decoding="async" src={IMAGES.recycling} alt="EEWaste recycling facility" className="w-full h-full object-cover" />
            <div className="absolute bottom-5 left-5">
              <span className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-[#FBFAF8]/90 backdrop-blur border border-slate-200 font-mono-tech text-[11px] uppercase tracking-widest text-[#0B0F14]">
                <span className="w-2 h-2 rounded-full bg-emerald-600" />
                {t("rec.badge")}
              </span>
            </div>
          </motion.div>
        </div>

        <p className="font-mono-tech text-sm text-slate-500 mb-6 uppercase tracking-widest">
          {t("rec.handle")}
        </p>

        <motion.div
          variants={container}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-px bg-slate-200 border border-slate-200 rounded-2xl overflow-hidden"
        >
          {WASTE_TYPES.map((type, i) => (
            <motion.a
              href="#quote"
              key={type}
              variants={item}
              onMouseEnter={() => setHovered(i)}
              onMouseLeave={() => setHovered(null)}
              data-testid={`waste-type-${i}`}
              className="relative bg-[#FBFAF8] p-6 min-h-[130px] flex flex-col justify-between group hover:bg-slate-50 transition-colors"
            >
              <span className="font-mono-tech text-xs text-emerald-600/70">
                {String(i + 1).padStart(2, "0")}
              </span>
              <span className="font-display font-semibold text-[#0B0F14] text-sm sm:text-base leading-tight group-hover:text-emerald-600 transition-colors">
                {type}
              </span>
              <motion.span
                className="absolute bottom-0 left-0 h-0.5 bg-emerald-600"
                initial={{ width: 0 }}
                animate={{ width: hovered === i ? "100%" : 0 }}
                transition={{ duration: 0.3 }}
              />
            </motion.a>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
